import React, { useState, useEffect } from 'react';
import { FiX, FiSearch } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../../../services/api';
import '../../../styles/WhatsappModal.css';

/**
 * Modal "Reenviar a..." genérico — lista los contactos reales de WhatsApp del
 * número vinculado (búsqueda + selección múltiple) y envía lo que sea que
 * `sendEndpoint` genere (ticket de venta, estado de cuenta, etc.) a cada
 * contacto elegido. Compartido entre Ventas y Cobranzas para no duplicar
 * esta pantalla — cualquier mejora acá aplica a ambos módulos por igual.
 *
 * @param {string} title         Texto del encabezado del modal
 * @param {string} sendEndpoint  Ruta POST a la que se manda { ...sendBody, chatIds }
 * @param {object} sendBody      Datos propios del documento a enviar (ej. { saleId } o { clientId })
 * @param {() => void} onClose
 */
export default function ModalReenviarWhatsapp({ title, sendEndpoint, sendBody, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending]   = useState(false);
  const [results, setResults]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/whatsapp/contacts')
      .then(res => { if (!cancelled) setContacts(res.data.data || []); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || 'No se pudo cargar los contactos'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = contacts.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.number.includes(q);
  });

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSend = async () => {
    if (!selected.size) return;
    setSending(true);
    setResults(null);
    try {
      const res = await api.post(sendEndpoint, {
        ...sendBody,
        chatIds: Array.from(selected),
      });
      setResults(res.data.data.results);
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo enviar el documento');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wam-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wam-modal">
        <div className="wam-header">
          <span>{title}</span>
          <button className="wam-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="wam-body">
          {loading ? (
            <div className="wam-empty">Cargando contactos...</div>
          ) : error ? (
            <div className="wam-empty wam-error">{error}</div>
          ) : (
            <>
              <div className="wam-search-wrap">
                <FiSearch size={13} />
                <input
                  className="wam-search"
                  placeholder="Buscar contacto..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="wam-contact-list">
                {filtered.length === 0 ? (
                  <div className="wam-empty">Sin contactos</div>
                ) : filtered.map(c => (
                  <label key={c.id} className="wam-contact-row">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                    <span className="wam-contact-avatar"><FaWhatsapp size={13} /></span>
                    <span className="wam-contact-info">
                      <span className="wam-contact-name">{c.name}</span>
                      <span className="wam-contact-number">+{c.number}</span>
                    </span>
                  </label>
                ))}
              </div>
              {results && (
                <div className="wam-results">
                  {results.map(r => (
                    <div key={r.chatId} className={`wam-result-row ${r.ok ? 'ok' : 'fail'}`}>
                      {r.ok ? '✓' : '✗'} {contacts.find(c => c.id === r.chatId)?.name || r.chatId}
                      {!r.ok && r.error ? ` — ${r.error}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="wam-footer">
          <button className="wam-btn-send" onClick={handleSend} disabled={sending || !selected.size || loading || !!results}>
            <FaWhatsapp size={14} /> {sending ? 'Enviando...' : results ? 'Enviado' : `Enviar (${selected.size})`}
          </button>
          <button className="wam-btn-cerrar" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
